import {
  ColDef,
  ColGroupDef,
  SuppressHeaderKeyboardEventParams,
} from '@ag-grid-community/core';
import {
  AgGridHelperService,
  ProductColGroupId,
  ProductColId,
} from '@products/fund-listing/services/ag-grid-helper.service';
import { TranslateService } from '@shared/translate/translate.service';
import { ConfigurationId, SuppressDetails } from '@types';
import {
  FundListingPerformanceController,
  FundListingRatingsController,
  FundListingYieldsController,
} from '../controllers';

/**
 * Return default performance column definitions for US.
 * @param agGridHelperService ag grid helper service instance
 */
export function getDefaultPerformanceColumnDefsForUS(
  listingController: FundListingPerformanceController,
  suppressDetails: SuppressDetails,
  agGridHelperService: AgGridHelperService,
  translateService: TranslateService
): ColGroupDef[] {
  const colGroupDefs: ColGroupDef[] = [
    {
      groupId: ProductColGroupId.FUND,
      headerGroupComponent: 'calcTypeGroupHeaderComponent',
      headerGroupComponentParams: listingController,
      suppressHeaderKeyboardEvent: (
        params: SuppressHeaderKeyboardEventParams
      ) => {
        return agGridHelperService.suppressHeaderCellTabbing(
          params,
          ProductColGroupId.FUND
        );
      },
      children: [
        agGridHelperService.getFavFundColDef(listingController),
        agGridHelperService.getFundNameColDef(
          listingController.productType !== ConfigurationId.TIF &&
            listingController.productType !== ConfigurationId.CIT,
          {
            autoHeight: true,
          }
        ),
        agGridHelperService.getShareClassNameColDef(listingController),
      ],
    },
    {
      groupId: ProductColGroupId.NAV,
      headerGroupComponent: 'dateGroupHeaderComponent',
      headerGroupComponentParams: {
        asOfDateText: () => listingController.navAsOfDate,
        showAsOfPutnamLabel: !listingController.hasDataMigrated,
      },
      children: [
        agGridHelperService.getNavValueColDef(listingController, {
          width: 90,
          wrapText: false,
          cellStyle: { 'white-space': 'normal' },
        }),
        agGridHelperService.getNavChangeValueColDef(listingController, {
          width: 130,
          wrapText: false,
        }),
        agGridHelperService.getNavYTDReturnColDef(listingController, {
          width: 80,
          wrapText: false,
          cellStyle: { 'white-space': 'normal' },
        }),
      ],
    },
    {
      groupId: ProductColGroupId.PERFORMANCE,
      headerGroupComponent: 'performanceGroupHeaderComponent',
      headerGroupComponentParams: listingController,
      suppressHeaderKeyboardEvent: (
        params: SuppressHeaderKeyboardEventParams
      ) => {
        return agGridHelperService.suppressHeaderCellTabbing(
          params,
          ProductColGroupId.PERFORMANCE
        );
      },
      children: [
        agGridHelperService.getAvgAnnualMonthly1YrColDef(
          listingController,
          suppressDetails,
          {
            width:
              listingController.productType === ConfigurationId.TOF ? 90 : 75,
          }
        ),
        agGridHelperService.getAvgAnnualMonthly3YrColDef(listingController, {
          width:
            listingController.productType === ConfigurationId.TOF ? 90 : 75,
        }),
        agGridHelperService.getAvgAnnualMonthly5YrColDef(listingController, {
          width:
            listingController.productType === ConfigurationId.TOF ? 90 : 70,
        }),
        agGridHelperService.getAvgAnnualMonthly10YrColDef(listingController, {
          width:
            listingController.productType === ConfigurationId.TOF ? 90 : 70,
        }),
        agGridHelperService.getAvgAnnualMonthlyInceptionColDef(
          listingController,
          suppressDetails,
          {
            width:
              listingController.productType === ConfigurationId.TOF ||
              listingController.productType === ConfigurationId.MMF
                ? 120
                : 115,
          }
        ),
        agGridHelperService.getAvgAnnualQuarterly1YrColDef(
          listingController,
          suppressDetails,
          {
            width:
              listingController.productType === ConfigurationId.TOF ? 90 : 75,
          }
        ),
        agGridHelperService.getAvgAnnualQuarterly3YrColDef(listingController, {
          width:
            listingController.productType === ConfigurationId.TOF ? 90 : 75,
        }),
        agGridHelperService.getAvgAnnualQuarterly5YrColDef(listingController, {
          width:
            listingController.productType === ConfigurationId.TOF ? 90 : 70,
        }),
        agGridHelperService.getAvgAnnualQuarterly10YrColDef(listingController, {
          width:
            listingController.productType === ConfigurationId.TOF ? 90 : 70,
        }),
        agGridHelperService.getAvgAnnualQuarterlyInceptionColDef(
          listingController,
          suppressDetails,
          {
            width:
              listingController.productType === ConfigurationId.TOF ||
              listingController.productType === ConfigurationId.MMF
                ? 120
                : 115,
          }
        ),
        agGridHelperService.getExpenseRatioColDef(listingController, {
          minWidth: 115,
          maxWidth: 115,
        }),
        agGridHelperService.getFundDocumentColDef({
          headerName:
            listingController.productType === ConfigurationId.TIF
              ? translateService.instant('products.product-profile')
              : translateService.instant('products.fact-sheet'),
          width: 80,
        }),
      ],
    },
  ];

  // Filter out Expense Ratio column for TOF
  if (listingController.productType === ConfigurationId.TOF) {
    const performanceGroup: ColGroupDef = colGroupDefs.find(
      (group: ColGroupDef): boolean =>
        group.groupId === ProductColGroupId.PERFORMANCE
    );
    performanceGroup.children = performanceGroup.children.filter(
      (col: ColDef): boolean => col.colId !== ProductColId.EXPENSERATIO
    );
  }

  return colGroupDefs;
}

/**
 * Return default yield column definitions for intl.
 * @param listingController ppss tab component
 * @param agGridHelperService ag grid helper service instance
 * @param translateService translate service instance
 */
/**
 * NGC-9804 :- removing header group component for fund and Distribution groupID
 */
export function getDefaultYieldColumnDefsForUS(
  listingController: FundListingYieldsController,
  agGridHelperService: AgGridHelperService,
  translateService: TranslateService
): ColGroupDef[] {
  const colGroupDefs: ColGroupDef[] = [
    {
      groupId: ProductColGroupId.FUND,
      children: [
        agGridHelperService.getFavFundColDef(listingController),
        agGridHelperService.getFundNameColDef(
          listingController.productType !== ConfigurationId.CIT,
          { flex: 2 }
        ),
        agGridHelperService.getShareClassNameColDef(listingController),
      ],
    },
    {
      groupId: ProductColGroupId.DISTRIBUTION,
      // NGC-10993: Showing distribution as of date again.
      headerGroupComponent: 'dateGroupHeaderComponent',
      headerGroupComponentParams: {
        asOfDateText: () => listingController.navAsOfDate,
      },
      children: [
        agGridHelperService.getNAVDistributionRateColDef(listingController),
      ],
    },
    {
      groupId: ProductColGroupId.YIELD,
      headerGroupComponent: 'dateGroupHeaderComponent',
      headerGroupComponentParams: {
        asOfDateText: () => listingController.stdYieldThirtyDayAsofDate,
      },
      children: [
        agGridHelperService.get30DayStandardizedYieldWithoutWaiverColDef(
          listingController
        ),
        agGridHelperService.get30DayStandardizedYieldWithWaiverColDef(
          listingController
        ),
      ],
    },
    {
      groupId: ProductColGroupId.CHARGES,
      children: [agGridHelperService.getExpenseRatioColDef(listingController)],
    },
  ];
  return colGroupDefs;
}

/**
 * Return default rating column definitions for intl.
 * @param listingController ppss tab component
 * @param agGridHelperService ag grid helper service instance
 */
export function getDefaultRatingColumnDefsForUS(
  listingController: FundListingRatingsController,
  agGridHelperService: AgGridHelperService
): ColGroupDef[] {
  return [
    {
      groupId: ProductColGroupId.FUND,
      children: [
        agGridHelperService.getFavFundColDef(listingController),
        agGridHelperService.getFundNameColDef(
          listingController.productType !== ConfigurationId.CIT,
          { flex: 2 }
        ),
        agGridHelperService.getShareClassNameColDef(listingController),
      ],
    },
    {
      groupId: ProductColGroupId.MORNINGSTAR,
      headerGroupComponent: 'ratingsGroupHeaderComponent',
      headerGroupComponentParams: listingController,
      headerClass: 'ag-header-group-cell--noflex',
      children: [
        agGridHelperService.getMorningStarCategoryColDef(listingController),
        agGridHelperService.getMorningStarOverallRatingColDef(
          listingController,
          {
            flex: 0,
          }
        ),
        agGridHelperService.getMorningStar3yrRatingColDef(),
        agGridHelperService.getMorningStar5yrRatingColDef(),
        agGridHelperService.getMorningStar10yrRatingColDef(),
      ],
    },
  ];
}
